// How the assistant names people in a reply (agent-service `personLinkFragment` / `personChoiceFragment`):
// `[Full Name](person:<id>)` for a person it holds the id of, and, when it asks the user which person they
// mean, `[Full Name](person:?choose)` or `[Full Name](person:<id>?choose)` for each candidate. A choice may
// name only the person: the client looks the name up, so the model never has to invent an id.

export interface PersonMention {
  name: string;
  id?: string;
  choose: boolean;
}

const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
// The href alone, as react-markdown hands it over.
export const PERSON_HREF = new RegExp(`^person:(${UUID})?(\\?choose)?$`);
const PERSON_LINK = new RegExp(`\\[([^\\]\\n]+)\\]\\(person:(${UUID})?(\\?choose)?\\)`, "g");

/** The people a reply names, each once (by id, else by name), in the order they first appear. */
export function personMentions(markdown: string): PersonMention[] {
  const seen = new Map<string, PersonMention>();
  for (const [, name, id, choose] of markdown.matchAll(PERSON_LINK)) {
    const label = name!.trim();
    const key = id ?? personNameKey(label);
    const known = seen.get(key);
    if (known) known.choose ||= Boolean(choose);
    else seen.set(key, { name: label, ...(id ? { id } : {}), choose: Boolean(choose) });
  }
  return [...seen.values()];
}

/** The reply with its person links as the names they show, for a file or a prompt where no card is. */
export function unlinkPersons(markdown: string): string {
  return markdown.replace(PERSON_LINK, (_match, name: string) => name);
}

/** The key a chosen person is remembered under: the name as the reply wrote it, case and spacing aside. */
export function personNameKey(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}
